import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

import { AppThemeProvider } from '@/context/app-theme-context';
import { FirebaseClientProvider } from '@/firebase';

// Import global styles
import '@/lib/i18n';
import './index.css';

declare global {
  interface Array<T> {
    findLast(predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: unknown): T | undefined;
    findLastIndex(predicate: (value: T, index: number, array: T[]) => unknown, thisArg?: unknown): number;
  }
  interface Window {
    Stripe?: (key: string) => void;
  }
}

// Polyfills for older browsers
if (!Array.prototype.findLast) {
  Array.prototype.findLast = function (predicate) {
    for (let i = this.length - 1; i >= 0; i--) {
      if (predicate(this[i], i, this)) return this[i];
    }
    return undefined;
  };
}
if (!Array.prototype.findLastIndex) {
  Array.prototype.findLastIndex = function (predicate) {
    for (let i = this.length - 1; i >= 0; i--) {
      if (predicate(this[i], i, this)) return i;
    }
    return -1;
  };
}

// --- Stripe Fraud Prevention Initialization ---
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (stripeKey && window.Stripe) {
  window.Stripe(stripeKey);
}

// Create a new router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <AppThemeProvider>
        <FirebaseClientProvider>
          <RouterProvider router={router} />
        </FirebaseClientProvider>
      </AppThemeProvider>
    </StrictMode>
  );
}
